import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { HubIcon } from '@atlaskit/logo';
import { LinkMenuItem, type LinkMenuItemProps } from '@atlaskit/side-nav-items/link-menu-item';

import type { GlobalItemProps } from '../globalItemProps.ts';

import { AbstractCompanyHubItem } from './AbstractCompanyHubItem.tsx';

const DEFAULT_COMPANY_HUB_NAME = 'Company Hub';

const i18n = defineMessages({
	CompanyHubName: {
		id: 'side-navigation.custom.company-hub.shortcut-name',
		description:
			'The text is shown as the label for the Company Hub navigation shortcut item in the global side navigation panel. This is the display name for the link to the company hub.',
		defaultMessage: 'Company Hub',
	},
});

export const CompanyHubItem: FC<CompanyHubItemProps> = ({ isHidden, onClick }) => {
	// This item can only be hidden if it's an app shortcut, but then it doesn't peek because it opens in a new tab.
	if (isHidden) {
		return null;
	}

	return (
		<AbstractCompanyHubItem navVersion="4">
			{({ companyHubName, isSelected, namedRoute, onClick: onAbstractCompanyHubItemClick }) => {
				const target: LinkMenuItemProps['target'] = '_blank';
				// https://hello.atlassian.net/wiki/spaces/DST/pages/4992197568/Company+Hub+Team+25+App+Tile+update+worksheet and
				// https://www.figma.com/design/MrViCsb93b2UqRjYrGQgaD/Confluence-Griffin-nav?node-id=9932-52111&t=B9QrCfJUvgDZwy64-0
				// say the icon should be 20px.
				const elemBefore: LinkMenuItemProps['elemBefore'] = <HubIcon size="xsmall" />;
				const elemAfter: LinkMenuItemProps['elemAfter'] = (
					<LinkExternalIcon label="" spacing="spacious" size="small" />
				);

				const onMenuLinkItemClick: LinkMenuItemProps['onClick'] = () => {
					onClick?.();
					onAbstractCompanyHubItemClick();
				};

				const isDefaultCompanyHubName = companyHubName === DEFAULT_COMPANY_HUB_NAME;

				return (
					<LinkMenuItem
						elemAfter={elemAfter}
						elemBefore={elemBefore}
						href={namedRoute?.toUrl() || '#'}
						isSelected={isSelected}
						onClick={onMenuLinkItemClick}
						target={target}
					>
						{isDefaultCompanyHubName ? (
							<FormattedMessage {...i18n.CompanyHubName} />
						) : (
							companyHubName
						)}
					</LinkMenuItem>
				);
			}}
		</AbstractCompanyHubItem>
	);
};

type CompanyHubItemProps = GlobalItemProps & {
	onClick?: () => void;
};
